import React from 'react';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import ChartCard from '@ui/chart/ChartCard';
import DataTable from '@ui/chart/DataTable';
import Heatmap from '@ui/chart/Heatmap';
import BarChart from '@ui/chart/BarChart';
import { barChartTable, heatmapTable } from '@ui/chart/chartTable';
import { RevenueScope, RoomTypeReport, VatBasis } from '@app/model/bi/Bi';
import { formatMonthRange, MonthRange } from '@ui/selector/MonthRangePicker';
import { revenueName } from '../Bi.utilities';
import { heatmapFooter, heatmapMax, heatmapRows, Measure, mix, monthLabel, occupancyWords, sortedRows } from './RoomTypes.utilities';

type State = 'default' | 'loading' | 'refreshing' | 'error';

const MEASURES: { value: Measure, label: string }[] = [
	{ value: 'occupancy', label: 'Occupancy' },
	{ value: 'revenueShare', label: 'Revenue share' },
	{ value: 'roomNightsShare', label: 'Room-nights share' },
];
const MEASURE_NAME: Record<Measure, string> = { occupancy: 'Occupancy', revenueShare: 'Revenue share', roomNightsShare: 'Room-nights share' };

// The months of the period, YYYY-MM.
export const monthsOf = (period: MonthRange): string[] => {
	const out: string[] = [];
	for (let y = Number(period.from.slice(0, 4)), m = Number(period.from.slice(5, 7)); `${y}-${String(m).padStart(2, '0')}` <= period.to;) {
		out.push(`${y}-${String(m).padStart(2, '0')}`);
		m += 1;
		if (m === 13) { m = 1; y += 1; }
	}
	return out;
};

type GridProps = {
	report: RoomTypeReport | undefined,
	// ROO2-272 D8: the report before "All other" (decision 22): the total row counts every room type with rooms, also the types
	// inside "All other". Before, a type with rooms inside "All other" dropped out of Total occ.
	totalReport?: RoomTypeReport,
	period: MonthRange,
	asOf: string,
	measure: Measure,
	onMeasure: (measure: Measure) => void,
	scope: RevenueScope,
	vat: VatBasis,
	state: State,
	onRetry: () => void,
	// ROO2-302 (Q1 C): the note that the held rooms of group blocks are not in this report yet (heldBlocksNote); null without
	// them. Analysis is the default view, so the note shows here too, not only under the tables.
	heldNote?: string | null,
};

// The grid of the Analysis view (RQ-08 … RQ-12): one row per room type with sold nights, one column per month, the Year
// column and the total row. The current month carries ● (RQ-11); with months of two years each heading carries the year.
export const RoomTypeGridCard: React.FunctionComponent<GridProps> = ({ report, totalReport, period, asOf, measure, onMeasure, scope, vat, state, onRetry, heldNote = null }) => {
	const months = monthsOf(period);
	const twoYears = period.from.slice(0, 4) !== period.to.slice(0, 4);
	const columns = months.map(m => `${monthLabel(m, twoYears)}${m === asOf.slice(0, 7) ? ' ●' : ''}`);
	const rows = report === undefined ? [] : heatmapRows(report, months, measure, scope, vat);
	const noInventory = report !== undefined && measure === 'occupancy' && report.rows.every(r => r.roomDays === 0);
	const basis = report?.basis === 'ASSIGNED' ? 'assigned' : 'booked';
	// ROO2-315 part c1 (Q3): with report rules, the occupied rooms ÷ the room-days less the out-of-order rooms of the type.
	const words = occupancyWords(report);
	const formula = words.rooms === '' ? `${words.occupied} ÷ room-days of the type in the month` : `${words.occupied} ÷ (room-days of the type in the month${words.rooms})`;
	const caption = measure === 'occupancy'
		? (report?.basis === 'ASSIGNED'
			? `Occupancy = ${formula}. The rooms of a month come from the bookings of that month; the label shows the rooms of the room list. On the Assigned basis each night is in the room that the guest used.`
			: `Occupancy = ${formula}. The rooms of a month come from the bookings of that month; the label shows the rooms of the room list. Occupancy counts the room that the hotel assigned (Assigned room type) and only room types with rooms. A night booked on a virtual room type (Overbooking) shows under its assigned room type.`)
		: `Share of the month = the type’s ${measure === 'revenueShare' ? revenueName(scope, vat) : 'room nights'} in the month ÷ all types’ in the month. n/a = no sold night of the type in the month.`;
	return <ChartCard title={`${MEASURE_NAME[measure]} by room type and month`}
		period={`${formatMonthRange(period)} · ${basis} room type · ● current month`}
		state={state !== 'loading' && state !== 'error' && report !== undefined && rows.length === 0 ? 'empty' : state} onRetry={onRetry}
		emptyLabel="No room-type data. No room-type activity in the selected period."
		actions={<ButtonTabs label="Measure of the grid" options={MEASURES} value={measure} onChange={v => onMeasure(v as Measure)} />}
		table={report === undefined || noInventory ? undefined
			: <DataTable {...heatmapTable({ columns, rows, rowLabel: 'Room type', totalLabel: 'Year', format: 'percent' })} framed={false} ariaLabel={`${MEASURE_NAME[measure]} by room type and month`} />}
		caption={heldNote === null ? caption : <>{caption}<br />{heldNote}</>}>
		{noInventory
			? <p className="py-8 text-center text-sm text-gray-500">Occupancy needs room inventory. The Revenue share and the Room-nights share stay available.</p>
			: report !== undefined && <Heatmap columns={columns} rows={rows} format="percent" rowHeader="Room type" totalHeader="Year" closedLabel="n/a"
				min={0} max={heatmapMax(rows, measure)} footer={heatmapFooter(totalReport ?? report, months, measure, scope, vat)}
				ariaLabel={`${MEASURE_NAME[measure]} by room type and month, ${formatMonthRange(period)}`} />}
	</ChartCard>;
};

type MixProps = {
	report: RoomTypeReport | undefined,
	period: MonthRange,
	scope: RevenueScope,
	vat: VatBasis,
	state: State,
	onRetry: () => void,
};

// The mix (RQ-13, decision 9): one horizontal bar chart, the room types as rows, three shares side by side.
export const RoomTypeMixCard: React.FunctionComponent<MixProps> = ({ report, period, scope, vat, state, onRetry }) => {
	const data = report === undefined ? null : mix(sortedRows(report.rows, scope, vat), report.total, scope, vat);
	const typed = report?.rows.filter(r => r.rooms !== null && r.rooms > 0) ?? [];
	const rooms = typed.reduce((s, r) => s + (r.rooms ?? 0), 0);
	return <ChartCard title="Room type mix"
		period={`${formatMonthRange(period)} · ${rooms === 0 ? 'no room inventory, so no inventory share' : `${rooms} rooms in ${typed.length} room types`}`}
		state={state !== 'loading' && state !== 'error' && data !== null && data.categories.length === 0 ? 'empty' : state} onRetry={onRetry}
		emptyLabel="No room-type data. No room-type activity in the selected period."
		table={data === null ? undefined : <DataTable {...barChartTable({ ...data, categoryLabel: 'Room type', format: 'percent', decimals: 1 })} framed={false} ariaLabel="Room type mix" />}
		caption="Inventory share = room-days of the type in the period ÷ the room-days of all types. A type with no room-days in the period has no inventory share.">
		{data !== null && <BarChart categories={data.categories} series={data.series} format="percent" decimals={1}
			ariaLabel={`Room type mix: inventory, room nights and revenue, ${formatMonthRange(period)}`} />}
	</ChartCard>;
};

